import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import {
  AuthControllerLogin200Response,
  AuthControllerLogout200Response,
  LoginDto,
  UserDto,
  UsersControllerGetProfile200Response
} from "@be/models";
import { BehaviorSubject, Observable } from "rxjs";
import { finalize, map, switchMap, tap } from "rxjs/operators";
import { environment } from "../../../../environments/environment";
import { ROUTE } from "../../../shared/utils/routes";
import { TokenService } from "../token/token.service";

@Injectable({
  providedIn: "root"
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly tokenService = inject(TokenService);
  private readonly baseUrl = `${environment.backendUrl}/auth`;

  private readonly isLoggedInSubject = new BehaviorSubject<boolean>(
    this.checkLoginStatus()
  );
  isLoggedIn$ = this.isLoggedInSubject.asObservable();

  private readonly userProfileSubject = new BehaviorSubject<UserDto | null>(
    null
  );
  userProfile$ = this.userProfileSubject.asObservable();

  constructor() {
    if (this.checkLoginStatus()) {
      this.fetchUserProfile().subscribe();
    }
  }

  login(credentials: LoginDto): Observable<void> {
    return this.http
      .post<AuthControllerLogin200Response>(
        `${this.baseUrl}/login`,
        credentials
      )
      .pipe(
        tap((response) => {
          const { accessToken, refreshToken } = response.data;
          if (accessToken) this.tokenService.setAccessToken(accessToken);
          if (refreshToken) this.tokenService.setRefreshToken(refreshToken);
          this.isLoggedInSubject.next(true);
        }),
        switchMap(() => this.fetchUserProfile()),
        map(() => undefined)
      );
  }

  logout(): void {
    this.http
      .post<AuthControllerLogout200Response>(`${this.baseUrl}/logout`, {})
      .pipe(
        finalize(() => {
          this.tokenService.clearTokens();
          this.isLoggedInSubject.next(false);
          this.userProfileSubject.next(null);
          this.router.navigate([ROUTE.LOGIN]);
        })
      )
      .subscribe();
  }

  isLoggedIn(): boolean {
    return this.checkLoginStatus();
  }

  getUserProfile(): UserDto | null {
    return this.userProfileSubject.value;
  }

  refreshUserProfile(): Observable<void> {
    return this.fetchUserProfile();
  }

  private checkLoginStatus(): boolean {
    const token = this.tokenService.getAccessToken();
    return !!token;
  }

  private fetchUserProfile(): Observable<void> {
    return this.http
      .get<UsersControllerGetProfile200Response>(
        `${environment.backendUrl}/users/profile`
      )
      .pipe(
        tap((response) => {
          this.userProfileSubject.next(response.data);
        }),
        map(() => undefined)
      );
  }
}
